import React, { useEffect, useState, useRef } from "react";
import logo from "../../resource/7.png";
import CommonFormFields from "./CommonFormFields";
import Swal from "sweetalert2";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import apiClient from "@/api/client";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Australia/Melbourne");

const DanaPaymentRequest = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    wtNumber: "",
    email: "",
    date: "",
    danaType: "",
    ip_address: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [loadingDates, setLoadingDates] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs.tz());
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [cutoffPassed, setCutoffPassed] = useState(false);

  const calendarRef = useRef(null);
  const inputRef = useRef(null);

  const toYMD = (dateString) => dayjs.tz(dateString).format("YYYY-MM-DD");

  const isDateOutOfRange = (dateString) => {
    const today = dayjs.tz().startOf("day");
    const maxDate = today.add(1, "year");
    const date = dayjs.tz(dateString).startOf("day");
    return date.isAfter(maxDate, "day");
  };

  // Derived: which meal types are currently selected
  const selectedMealTypes = (() => {
    switch (formData.danaType) {
      case "breakfast": return ["breakfast"];
      case "lunch":     return ["lunch"];
      default:          return [];
    }
  })();

  // Cutoff: earliest of the selected meals wins.
  useEffect(() => {
    const checkCutoff = () => {
      if (selectedMealTypes.length === 0) {
        setCutoffPassed(false);
        return;
      }
      const now = dayjs.tz();
      let earliest = null;
      if (selectedMealTypes.includes("breakfast")) {
        earliest = dayjs.tz().hour(6).minute(0).second(0);
      }
      if (selectedMealTypes.includes("lunch")) {
        const lunchCutoff = dayjs.tz().hour(11).minute(0).second(0);
        if (!earliest || lunchCutoff.isBefore(earliest)) earliest = lunchCutoff;
      }
      setCutoffPassed(now.isAfter(earliest));
    };
    checkCutoff();
    const interval = setInterval(checkCutoff, 30000);
    return () => clearInterval(interval);
  }, [formData.danaType]);

  // Fetch IP
  useEffect(() => {
    const fetchIP = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        setFormData((prev) => ({ ...prev, ip_address: response.data.ip }));
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };
    fetchIP();
  }, []);

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCustomCalendar &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowCustomCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCustomCalendar]);

  // Aggregate unavailable dates across all selected meal types
  const fetchUnavailableDates = async (mealTypes) => {
    if (!mealTypes.length) return [];

    const res = await apiClient.get("/get-dana-calendar-summary");
    const data = res.data?.data || {};

    const unavailableSet = new Set();
    for (const mealType of mealTypes) {
      const meal = data[mealType] || { booked: [], blocked: [] };
      const bookedList = Array.isArray(meal.booked) ? meal.booked : [];
      const blockedList = Array.isArray(meal.blocked) ? meal.blocked : [];

      for (const item of [...bookedList, ...blockedList]) {
        const dateVal = item.dana_event_date || item.date;
        if (dateVal) unavailableSet.add(toYMD(dateVal));
      }
    }
    return Array.from(unavailableSet);
  };

  const refreshUnavailableDates = async () => {
    if (selectedMealTypes.length === 0) {
      setBlockedDates(new Set());
      setAvailabilityError(null);
      return;
    }

    setLoadingDates(true);
    setAvailabilityError(null);

    try {
      const unavailable = await fetchUnavailableDates(selectedMealTypes);
      const unavailableSet = new Set(unavailable);
      setBlockedDates(unavailableSet);

      if (formData.date && unavailableSet.has(formData.date)) {
        setFormData((prev) => ({ ...prev, date: "" }));
        setErrors((prev) => ({
          ...prev,
          date: "This date is no longer available. Please select another date.",
        }));
        setDuplicateError(
          "The selected date is no longer available. Please choose another date."
        );
        setShowCustomCalendar(false);
      } else if (formData.date && !unavailableSet.has(formData.date)) {
        setDuplicateError(null);
      }
    } catch (error) {
      console.error("Error refreshing unavailable dates:", error);
      setAvailabilityError(
        error.message || "Failed to load available dates. Date selection is disabled."
      );
      setBlockedDates(new Set());
    } finally {
      setLoadingDates(false);
    }
  };

  // Refetch when Dana type changes
  useEffect(() => {
    refreshUnavailableDates();
  }, [formData.danaType]);

  // Refetch when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      if (selectedMealTypes.length > 0) refreshUnavailableDates();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [formData.danaType, formData.date]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "date" && errors.date) {
      setErrors((prev) => ({ ...prev, date: null }));
      setDuplicateError(null);
    }

    if (name === "danaType") {
      setDuplicateError(null);
      setAvailabilityError(null);
      setFormData((prev) => ({ ...prev, date: "" }));
      setErrors((prev) => ({ ...prev, date: null, danaType: null }));
    }
  };

  const isDateBlocked = (dateString) => {
    if (!dateString) return false;
    const selectedDate = dayjs.tz(dateString).startOf("day");
    const today = dayjs.tz().startOf("day");
    if (selectedDate.isBefore(today)) return true;
    if (isDateOutOfRange(dateString)) return true;
    if (selectedDate.isSame(today, "day") && cutoffPassed) return true;
    return blockedDates.has(dateString);
  };

  const handleDateSelect = (dateString) => {
    if (isDateBlocked(dateString)) {
      setErrors((prev) => ({
        ...prev,
        date: "This date is either in the past, beyond one year, or already booked. Please select another date.",
      }));
      setDuplicateError("This date is not available. Please select another date.");
      setFormData((prev) => ({ ...prev, date: "" }));
    } else {
      setFormData((prev) => ({ ...prev, date: dateString }));
      setErrors((prev) => ({ ...prev, date: null }));
      setDuplicateError(null);
      setShowCustomCalendar(false);
    }
  };

  const generateCalendarDays = () => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const startOfCalendar = startOfMonth.startOf("week");
    const endOfCalendar = endOfMonth.endOf("week");
    const days = [];
    let currentDay = startOfCalendar;
    while (currentDay.isBefore(endOfCalendar) || currentDay.isSame(endOfCalendar, "day")) {
      days.push(currentDay);
      currentDay = currentDay.add(1, "day");
    }
    return days;
  };

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const calendarDays = generateCalendarDays();
  const today = dayjs.tz();

  const isPastDate = (date) => date.isBefore(today, "day");
  const handlePrevMonth = () => setCurrentMonth(currentMonth.subtract(1, "month"));
  const handleNextMonth = () => setCurrentMonth(currentMonth.add(1, "month"));

  const validateForm = () => {
    const newErrors = {};

    // Validate First Name
    if (!formData.firstName) newErrors.firstName = "First Name is required";
    else if (!/^[A-Za-z\s\-]+$/.test(formData.firstName)) newErrors.firstName = "First Name should contain only letters, spaces, or hyphens";

    // Validate Last Name
    if (!formData.lastName) newErrors.lastName = "Last Name is required";
    else if (!/^[A-Za-z\s\-]+$/.test(formData.lastName)) newErrors.lastName = "Last Name should contain only letters, spaces, or hyphens";

    // Validate Mobile Number
    if (!formData.mobileNumber) newErrors.mobileNumber = "Mobile Number is required";
    else if (!/^\d{10}$/.test(formData.mobileNumber)) newErrors.mobileNumber = "Mobile Number should be 10 digits";

    // Validate Email
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email format";

    // Validate WT Number
    if (!formData.wtNumber) newErrors.wtNumber = "WhatsApp Number is required";
    else if (!/^\d{10}$/.test(formData.wtNumber)) newErrors.wtNumber = "WhatsApp Number should be 10 digits";

    if (!formData.danaType) {
      newErrors.danaType = "Please select Dana type";
    }

    if (!formData.date) newErrors.date = "Please select a date";
    else if (isDateBlocked(formData.date)) {
      newErrors.date = "This date is not available. Please select another date.";
    }

    if (availabilityError) {
      newErrors.date =
        "Date availability could not be verified. Please try again later or contact support.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please check the form for errors.",
      });
      return;
    }

    if (availabilityError) {
      Swal.fire({
        icon: "error",
        title: "Cannot Submit",
        text: "Date availability information is currently unavailable. Please refresh the page or try again later.",
      });
      return;
    }

    setLoading(true);
    setDuplicateError(null);

    try {
      // Derive the boolean flags the backend expects from the dropdown selection
      const dana_for_morning = formData.danaType === "breakfast";
      const dana_for_lunch   = formData.danaType === "lunch";

    const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNumber: formData.mobileNumber,
        wtNumber: formData.wtNumber,
        email: formData.email,
        date: formData.date,
        meal_type: formData.danaType === "Dana for Breakfast" ? "breakfast" : "lunch",
        ip_address: formData.ip_address,
    };

      await apiClient.post("/store-dana-payment-request", payload);

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Form submitted successfully!",
        confirmButtonColor: "#c49b36",
      });

      setFormData({
        firstName: "",
        lastName: "",
        mobileNumber: "",
        wtNumber: "",
        email: "",
        date: "",
        danaType: "",
        ip_address: formData.ip_address,
      });
      setBlockedDates(new Set());
      setShowCustomCalendar(false);
      setDuplicateError(null);
      setErrors({});
      setAvailabilityError(null);
    } catch (error) {
      console.error("Error submitting form:", error);
      let errorMessage = "Something went wrong!";

      if (error.response && error.response.status === 409) {
        const errorData = error.response.data;
        errorMessage =
          errorData.message || "This date is already booked. Please select another date.";

        await refreshUnavailableDates();
        setFormData((prev) => ({ ...prev, date: "" }));
        setDuplicateError(errorMessage);

        Swal.fire({
          icon: "warning",
          title: "Date Already Booked",
          text: errorMessage,
          confirmButtonText: "Choose Another Date",
          confirmButtonColor: "#c49b36",
        });

        if (inputRef.current) {
          inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          setShowCustomCalendar(true);
        }
      } else if (error.response && error.response.status === 422) {
        if (error.response.data.errors) {
          const backendErrors = {};
          Object.keys(error.response.data.errors).forEach((key) => {
            const fieldMap = {
              firstName: "firstName",
              lastName: "lastName",
              mobileNumber: "mobileNumber",
              email: "email",
              wtNumber: "wtNumber",
              date: "date",
              // backend may still report these keys – fold them into danaType
              dana_for_morning: "danaType",
              dana_for_lunch: "danaType",
              danaType: "danaType",
            };
            const frontendField = fieldMap[key] || key;
            backendErrors[frontendField] = error.response.data.errors[key][0];
          });
          setErrors((prev) => ({ ...prev, ...backendErrors }));
          errorMessage = "Please check the form for errors.";
        } else {
          errorMessage = error.response.data.message || "Validation error occurred.";
        }
        Swal.fire({ icon: "error", title: "Validation Error", text: errorMessage });
      } else if (error.response) {
        errorMessage =
          error.response.data.message || "An error occurred while submitting the form.";
        Swal.fire({ icon: "error", title: "Error", text: errorMessage });
      } else if (error.request) {
        errorMessage = "No response from the server. Please try again later.";
        Swal.fire({ icon: "error", title: "Connection Error", text: errorMessage });
      } else {
        Swal.fire({ icon: "error", title: "Oops...", text: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const hasSelectedMeal = selectedMealTypes.length > 0;

  return (
    <div className="form-page dark-gold" style={{ paddingTop: 10, marginTop: 10 }}>
      <form onSubmit={handleSubmit} style={{ marginTop: 10, paddingTop: 10 }}>
        <div className="form-title">
          <img src={logo} alt="" width={200} />
          <div className="details">
            <h1 className="font-second">Bhaddeka Vihari Monastery Center</h1>
            <h2 className="h4">Dana Payment Request Form</h2>
          </div>
        </div>

        <p className="font-p mt-6">
          Please fill in the following details and we will contact you.
        </p>

        <CommonFormFields
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        <p className="font-p mt-5">
          Please select the appropriate option for your Dana request.
        </p>

        <div className="form-box mt-4">
          <div className="form-item">
            <select
              id="danaType"
              name="danaType"
              value={formData.danaType}
              onChange={handleInputChange}
            >
              <option value="">Select Dana Type</option>
              <option value="breakfast">Dana for Breakfast</option>
              <option value="lunch">Dana for Lunch</option>
            </select>
            {errors.danaType && <span className="error">{errors.danaType}</span>}
          </div>
        </div>

        <p className="font-p mt-5">
          Kindly specify the date on which you wish to participate in the Dana event.
        </p>

        {duplicateError && (
          <div
            className="duplicate-error"
            style={{
              backgroundColor: "#ffebee",
              border: "1px solid #f44336",
              borderRadius: "4px",
              padding: "10px",
              marginTop: "10px",
              marginBottom: "10px",
              color: "#c62828",
              textAlign: "center",
            }}
          >
            <strong>⚠️ {duplicateError}</strong>
          </div>
        )}

        <div className="form-box mt-4">
          <div className="form-item">
            <div style={{ position: "relative" }}>
              <input
                ref={inputRef}
                type="text"
                placeholder={
                  availabilityError
                    ? "Date selection unavailable – see error below"
                    : hasSelectedMeal
                    ? "Click to select date"
                    : "Please select Dana type first"
                }
                value={formData.date ? new Date(formData.date).toLocaleDateString() : ""}
                onClick={() => {
                  if (hasSelectedMeal && !availabilityError && !loadingDates) {
                    setShowCustomCalendar(!showCustomCalendar);
                  }
                }}
                readOnly
                className={errors.date ? "error-input" : ""}
                style={{
                  cursor:
                    hasSelectedMeal && !availabilityError && !loadingDates
                      ? "pointer"
                      : "not-allowed",
                  width: "100%",
                  padding: "10px",
                  border: `1px solid ${availabilityError ? "#f44336" : "#ddd"}`,
                  borderRadius: "4px",
                  fontSize: "16px",
                  backgroundColor: availabilityError ? "#ffebee" : "white",
                }}
                disabled={!hasSelectedMeal || loadingDates || !!availabilityError}
              />

              {loadingDates && (
                <span
                  className="info"
                  style={{ fontSize: "12px", marginTop: "5px", display: "block" }}
                >
                  Loading available dates...
                </span>
              )}

              {availabilityError && (
                <div style={{ marginTop: "8px" }}>
                  <span
                    className="error"
                    style={{ color: "#d32f2f", fontSize: "13px", display: "block" }}
                  >
                    ⚠️ {availabilityError}
                  </span>
                  <button
                    type="button"
                    onClick={refreshUnavailableDates}
                    style={{
                      marginTop: "5px",
                      background: "none",
                      border: "1px solid #c49b36",
                      borderRadius: "4px",
                      padding: "4px 12px",
                      fontSize: "12px",
                      cursor: "pointer",
                      color: "#c49b36",
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}

              {showCustomCalendar && hasSelectedMeal && !availabilityError && !loadingDates && (
                <div
                  ref={calendarRef}
                  className="custom-calendar-container"
                  style={{
                    position: "absolute",
                    zIndex: 1000,
                    background: "white",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    padding: "15px",
                    width: "320px",
                    backgroundColor: "#fff",
                    left: "0",
                    bottom: "100%",
                    marginBottom: "5px",
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "15px",
                      padding: "0 5px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={handlePrevMonth}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "18px",
                        cursor: "pointer",
                        padding: "5px 10px",
                        color: "#c49b36",
                        fontWeight: "bold",
                      }}
                    >
                      ←
                    </button>
                    <span style={{ fontWeight: "bold", fontSize: "16px", color: "#333" }}>
                      {currentMonth.format("MMMM YYYY")}
                    </span>
                    <button
                      type="button"
                      onClick={handleNextMonth}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "18px",
                        cursor: "pointer",
                        padding: "5px 10px",
                        color: "#c49b36",
                        fontWeight: "bold",
                      }}
                    >
                      →
                    </button>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      marginBottom: "10px",
                      gap: "2px",
                    }}
                  >
                    {weekDays.map((day) => (
                      <div
                        key={day}
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: "bold",
                          padding: "8px 0",
                          color: "#666",
                        }}
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      gap: "2px",
                    }}
                  >
                    {calendarDays.map((day) => {
                      const dateString = day.format("YYYY-MM-DD");
                      const isBlocked = blockedDates.has(dateString);
                      const isPast = isPastDate(day);
                      const isOutOfRange = isDateOutOfRange(dateString);
                      const isSelected = formData.date === dateString;
                      const isToday = day.isSame(today, "day");
                      const isCutoffDisabled = isToday && cutoffPassed;
                      const isDisabled =
                        isPast || isOutOfRange || isBlocked || isCutoffDisabled;

                      return (
                        <div
                          key={dateString}
                          onClick={() => !isDisabled && handleDateSelect(dateString)}
                          style={{
                            aspectRatio: "1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            fontSize: "14px",
                            borderRadius: "4px",
                            transition: "all 0.2s",
                            backgroundColor: isSelected
                              ? "#c49b36"
                              : (isBlocked || isCutoffDisabled) && !isPast && !isOutOfRange
                              ? "#ffe6e6"
                              : isPast || isOutOfRange
                              ? "#f0f0f0"
                              : "white",
                            color: isSelected
                              ? "white"
                              : isPast || isOutOfRange
                              ? "#ccc"
                              : isBlocked || isCutoffDisabled
                              ? "#999"
                              : "#333",
                            opacity: isDisabled ? 0.5 : 1,
                            border: "none",
                          }}
                          onMouseEnter={(e) => {
                            if (!isDisabled && !isSelected)
                              e.currentTarget.style.backgroundColor = "#f4e4a1";
                          }}
                          onMouseLeave={(e) => {
                            if (!isDisabled && !isSelected)
                              e.currentTarget.style.backgroundColor = "white";
                          }}
                        >
                          {day.date()}
                        </div>
                      );
                    })}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                      marginTop: "15px",
                      paddingTop: "10px",
                      borderTop: "1px solid #eee",
                      fontSize: "11px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "3px",
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                        }}
                      ></div>
                      <span>Available</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "3px",
                          backgroundColor: "#ffe6e6",
                          border: "1px solid #f44336",
                        }}
                      ></div>
                      <span>Booked/Blocked</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {errors.date && <span className="error">{errors.date}</span>}
          </div>
        </div>

        <button
          type="submit"
          id="submitbtn"
          className="mt-5"
          disabled={loading || !!availabilityError}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>

        <p className="font-p mt-5">
          Once the Dana confirmation is received from the Monastery, you may proceed with making the necessary payments online.
        </p>
        <p className="font-p mt-4 pb-4">
          Thank you for your kind support. We will be in touch with you shortly.
        </p>
      </form>
    </div>
  );
};

export default DanaPaymentRequest;